import _ from "lodash";
import { useCallback } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  useCustomQueryParams,
  useDynamicFormsUtils,
  usePageContext,
} from "@ryde/redux";
import {
  companyIdSelector,
  getLastFinalSchemaLocalStore,
  gridSchemaSF,
  GROUPING_FIELD_PRODUCE_BUTTON,
  hasKeys,
  Log,
  NO_GROUPING_QS_VALUE,
  parseBoolean,
  ProduceButtonsPageViewMode,
  produceButtonsPageViewModeAtom,
  tableColumnsOrderStateAF,
  tableColumnsSizingAF,
  tableColumnsSortingAF,
  tableColumnsVisibilityAF,
  tableGroupingAF,
  tableGroupingDirectionDescAF,
  unpatchedVisibilityStateSF,
  UNRESOLVED_GRID_FIELD_PREFIX,
  useIsMobile,
  type ColumnOrderState,
  type GroupingState,
  type SortingState,
  type VisibilityState,
} from "@ryde/shared";

export const useDefaultTableGrouping = () => {
  const { entity } = usePageContext();

  const { tableGroupingFromQS: tableGroupingQs } = useCustomQueryParams();
  const { isMobile } = useIsMobile();

  const [columnSorting, setColumnSorting] = useRecoilState(
    tableColumnsSortingAF(entity),
  );
  const [columnGrouping, setColumnGrouping] = useRecoilState(
    tableGroupingAF(entity),
  );

  const [columnOrder, setColumnOrder] = useRecoilState(
    tableColumnsOrderStateAF(entity),
  );

  const [columnVisibility, setColumnVisibility] = useRecoilState(
    tableColumnsVisibilityAF(entity),
  );

  const [columnsSizing, setColumnsSizing] = useRecoilState(
    tableColumnsSizingAF(entity),
  );

  const setColumnGroupingDirectionDesc = useSetRecoilState(
    tableGroupingDirectionDescAF(entity),
  );
  const companyId = useRecoilValue(companyIdSelector);
  const finalSchema = getLastFinalSchemaLocalStore(companyId ?? 0, entity);

  const { getGridFieldNameByControlId } = useDynamicFormsUtils();
  const produceButtonPageView = useRecoilValue(produceButtonsPageViewModeAtom);

  const {
    groupBy: groupBySchema,
    columnsVisibility: columnsVisibilitySchema,
    sortBy: sortBySchema,
  } = useRecoilValue(gridSchemaSF(entity));
  const unpatchedVisibilityState = useRecoilValue(
    unpatchedVisibilityStateSF(entity),
  );

  const setSortingChecked = useEvent((updatedSorting: SortingState) => {
    if (!_.isEqual(columnSorting, updatedSorting)) {
      Log.debug("Update column sorting", columnSorting, updatedSorting);
      setColumnSorting(updatedSorting);
    }
  });

  const setColumnOrderChecked = useEvent((updatedOrder: ColumnOrderState) => {
    if (!_.isEqual(columnOrder, updatedOrder)) {
      Log.debug("Update column order", columnOrder, updatedOrder);
      setColumnOrder(updatedOrder);
    }
  });

  const setColumnVisibilityChecked = useEvent(
    (updatedVisibility: VisibilityState) => {
      if (!_.isEqual(columnVisibility, updatedVisibility)) {
        Log.debug(
          "Update column visibility",
          columnVisibility,
          updatedVisibility,
        );
        setColumnVisibility(updatedVisibility);
      }
    },
  );

  const setGroupingChecked = useCallback(
    (updatedGrouping: GroupingState) => {
      // Strip unresolved placeholders so a stale localStorage/URL value self-heals to "no grouping"
      // instead of being re-applied and re-pushed to the QS as `group=unresolved_*`.
      const sanitizedGrouping = updatedGrouping?.filter(
        x => x && !x.startsWith(UNRESOLVED_GRID_FIELD_PREFIX),
      );
      const groupValue = sanitizedGrouping?.length
        ? sanitizedGrouping
        : [NO_GROUPING_QS_VALUE];

      if (!_.isEqual(columnGrouping, groupValue)) {
        Log.debug("Update column grouping", columnGrouping, groupValue);
        setColumnGrouping(groupValue);
      }
    },
    [columnGrouping, setColumnGrouping],
  );

  // TODO refactor it, implicit loop in hook caused by `columnGrouping` memo dependency (for handleInitialGrouping) changed
  const handleProduceButtonGrouping = useCallback(() => {
    if (entity === "produceButton") {
      if (produceButtonPageView === ProduceButtonsPageViewMode.ViewAllButtons) {
        if (columnGrouping?.[0] !== GROUPING_FIELD_PRODUCE_BUTTON) {
          setGroupingChecked([GROUPING_FIELD_PRODUCE_BUTTON]);
        }

        return true;
      } else if (
        produceButtonPageView === ProduceButtonsPageViewMode.ViewButtonsOfToday
      ) {
        if (columnGrouping?.[0] === GROUPING_FIELD_PRODUCE_BUTTON) {
          setGroupingChecked([]);
        }

        return true;
      }
    }

    if (
      entity !== "produceButton" &&
      columnGrouping?.[0] === GROUPING_FIELD_PRODUCE_BUTTON
    ) {
      setGroupingChecked([]);
      return true;
    }

    return false;
  }, [columnGrouping, entity, produceButtonPageView, setGroupingChecked]);

  const handleInitialGrouping = useCallback(() => {
    if (columnGrouping?.length) {
      return;
    }

    if (tableGroupingQs?.length && finalSchema?.columnsGrouping) {
      // Take from final schema when grouping is present in query string
      setGroupingChecked(finalSchema.columnsGrouping);
    } else if (isMobile && groupBySchema?.length) {
      // Take from gird schema
      const groupById = Number(groupBySchema[0].id);
      const groupingField = Number.isFinite(groupById)
        ? getGridFieldNameByControlId(groupById)
        : "";
      const grouping = [groupingField].filter(
        x => x && !x.startsWith(UNRESOLVED_GRID_FIELD_PREFIX),
      );
      setGroupingChecked(grouping?.length ? grouping : [NO_GROUPING_QS_VALUE]);
    } else if (finalSchema?.columnsGrouping) {
      // On desktop, take from final schema (includes rideMap groupBy from grid schema)
      setGroupingChecked(finalSchema.columnsGrouping);
    }

    if (groupBySchema?.length) {
      setColumnGroupingDirectionDesc(groupBySchema[0].desc ?? false);
    }
  }, [
    columnGrouping,
    finalSchema,
    getGridFieldNameByControlId,
    groupBySchema,
    isMobile,
    setColumnGroupingDirectionDesc,
    setGroupingChecked,
    tableGroupingQs?.length,
  ]);

  const groupingComplicatedInit = useCallback(() => {
    if (handleProduceButtonGrouping()) {
      return;
    }

    handleInitialGrouping();
  }, [handleInitialGrouping, handleProduceButtonGrouping]);

  const handleInitialColumnVisibility = useCallback(() => {
    if (hasKeys(columnVisibility)) {
      return;
    }

    if (hasKeys(finalSchema?.columnsVisibility)) {
      // Take from final schema local storage
      const unpatchedNewVisibilityLocalStorage: VisibilityState = {
        ...unpatchedVisibilityState,
        ...finalSchema?.columnsVisibility,
      };

      setColumnVisibilityChecked(unpatchedNewVisibilityLocalStorage);
    } else if (
      columnsVisibilitySchema &&
      Object.keys(columnsVisibilitySchema)?.length
    ) {
      // Take from grid schema
      const newVisibilityPatch = {} as VisibilityState;
      Object.entries(columnsVisibilitySchema).forEach(([key, val]) => {
        const columnIdComputed = getGridFieldNameByControlId(Number(key));
        newVisibilityPatch[columnIdComputed] = parseBoolean(val);
      });

      const newVisibility = {
        ...unpatchedVisibilityState,
        ...newVisibilityPatch,
      };

      setColumnVisibilityChecked(newVisibility);
    }
  }, [
    columnVisibility,
    columnsVisibilitySchema,
    finalSchema?.columnsVisibility,
    getGridFieldNameByControlId,
    setColumnVisibilityChecked,
    unpatchedVisibilityState,
  ]);

  // Widths come only from the final schema local store, which a first visit fills from the default view.
  const handleInitialColumnSizing = useCallback(() => {
    if (hasKeys(columnsSizing) || !hasKeys(finalSchema?.columnsSizing)) {
      return;
    }

    setColumnsSizing(finalSchema?.columnsSizing ?? {});
  }, [columnsSizing, finalSchema?.columnsSizing, setColumnsSizing]);

  const handleInitialColumnSorting = useCallback(() => {
    if (columnSorting.length) {
      return;
    }

    if (isMobile && sortBySchema?.length) {
      // Take from grid schema
      const sortingRaw: SortingState = sortBySchema.map(s => ({
        id: getGridFieldNameByControlId(Number(s.id)),
        desc: s.desc ?? false,
      }));
      const sorting = sortingRaw.filter(x => x.id);

      setSortingChecked(sorting);
    } else if (finalSchema?.columnsSorting) {
      // Take from final schema
      setSortingChecked(finalSchema?.columnsSorting);
    }
  }, [
    columnSorting.length,
    finalSchema?.columnsSorting,
    getGridFieldNameByControlId,
    isMobile,
    setSortingChecked,
    sortBySchema,
  ]);

  return {
    setGroupingChecked,
    setColumnVisibilityChecked,
    setColumnOrderChecked,
    setSortingChecked,
    handleInitialGrouping,
    groupingComplicatedInit,
    handleInitialColumnVisibility,
    handleInitialColumnSizing,
    handleInitialColumnSorting,
  };
};
